// BookingRequests.js
import React, { useState, useEffect } from "react";
import {
  FaCheck,
  FaTimes,
  FaFilter,
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaGamepad,
  FaExclamationTriangle,
} from "react-icons/fa";
import axios from "axios";
import { useArenaAuth } from "../../context/ArenaAuthContext";
import { toast } from "react-toastify";
import ConfirmationModal from "../../common/ConfirmationModal";

const BookingRequests = () => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all"); // 'all', 'pending', 'approved', 'rejected'
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const { arenaUser } = useArenaAuth();

  const fetchBookingRequests = async () => {
    try {
      const arenaId = arenaUser?.arenaId;
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/arena-bookingRequests?arenaId=${arenaId}`,
        { credentials: "include" }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch booking requests");
      }
      const data = await response.json();
      setRequests(data);
      // console.log(data);
    } catch (error) {
      console.error("Error fetching booking requests:", error);
    }
  };

  console.log(requests);

  useEffect(() => {
    if (arenaUser?.arenaId) fetchBookingRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arenaUser?.arenaId]);

  const handleRefresh = () => {
    fetchBookingRequests(); // Refresh requests when called
  };

  const handleApprove = async (ids) => {
    try {
      // console.log("Approving ids:", ids);
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/approve-request`,
        { ids },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      console.log("Response data:", response.data);

      if (response.status === 200) {
        toast.success("Booking requests approved successfully");
        handleRefresh();
      }
    } catch (error) {
      console.error("Error approving booking requests:", error);
      toast.error("Error approving booking requests");
    }
  };

  const onApproveClick = (request) => {
    setSelectedRequest(request);
    setActionType("approve");
    setShowConfirmModal(true);
  };

  const onRejectClick = (request) => {
    setSelectedRequest(request);
    setActionType("reject");
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedRequest || !actionType) return;

    const ids = [selectedRequest.id];

    try {
      const endpoint =
        actionType === "approve"
          ? `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/approve-request`
          : `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/reject-request`;

      const response = await axios.put(
        endpoint,
        { ids },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      console.log("Response data:", response.data);

      if (response.status === 200) {
        toast.success(`Booking request ${actionType}d successfully`);
        handleRefresh();
        setShowConfirmModal(false);
        setSelectedRequest(null);
        setActionType(null);
      }
    } catch (error) {
      console.error(`Error ${actionType}ing booking request:`, error);
      toast.error(`Error ${actionType}ing booking request`);
      setShowConfirmModal(false);
      setSelectedRequest(null);
      setActionType(null);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setSelectedRequest(null);
    setActionType(null);
  };

  const isBookingTimePassed = (request) => {
    try {
      // Parse the time range (e.g., "09:00:00-09:30:00")
      const [startTime] = request.time.split("-");
      const bookingDateTime = new Date(`${request.date}T${startTime}`);
      const now = new Date();
      return bookingDateTime < now;
    } catch (error) {
      console.error("Error parsing booking time:", error);
      return false;
    }
  };

  const getEffectiveStatus = (request) => {
    // If status is pending and time has passed, show as "missed"
    if (
      request.status.toLowerCase() === "pending" &&
      isBookingTimePassed(request)
    ) {
      return "missed";
    }
    return request.status.toLowerCase();
  };

  const filteredRequests = requests.filter((request) => {
    if (filter === "all") return true;
    const effectiveStatus = getEffectiveStatus(request);
    return effectiveStatus === filter.toLowerCase();
  });

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
          <FaCalendarAlt className="text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
          <span className="truncate">Booking Requests</span>
        </h2>
        <p className="text-gray-600 text-xs sm:text-sm md:text-base">
          Review and manage facility booking requests
        </p>
      </div>

      <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 mb-4 sm:mb-6 md:mb-8">
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <FaFilter className="text-[#0071bc] text-sm sm:text-base" />
          <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
            Filter Requests
          </h3>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full md:w-auto px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 bg-white"
        >
          <option value="all">All Requests</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled by user">Cancelled by User</option>
          <option value="missed">Missed</option>
        </select>
      </div>

      <div className="space-y-3 sm:space-y-4 md:space-y-6">
        {filteredRequests.map((request) => {
          const effectiveStatus = getEffectiveStatus(request);
          const displayStatus =
            effectiveStatus === "missed"
              ? "Missed"
              : effectiveStatus === "cancelled by user"
              ? "Cancelled by User"
              : request.status;

          const getStatusColor = (status) => {
            switch (status) {
              case "pending":
                return "bg-yellow-100 text-yellow-800 border-yellow-200";
              case "approved":
                return "bg-green-100 text-green-800 border-green-200";
              case "rejected":
                return "bg-red-100 text-red-800 border-red-200";
              case "cancelled by user":
                return "bg-gray-100 text-gray-800 border-gray-200";
              case "missed":
                return "bg-orange-100 text-orange-800 border-orange-200";
              default:
                return "bg-gray-100 text-gray-800 border-gray-200";
            }
          };

          return (
            <div
              key={request.id}
              className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex flex-col gap-3 sm:gap-4 md:gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 md:mb-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                      <FaUser className="text-white text-xs sm:text-sm md:text-lg" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm sm:text-base md:text-xl font-bold text-gray-900 truncate">
                        {request.user}
                      </h3>
                      <div
                        className={`inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs md:text-sm font-medium border ${getStatusColor(
                          effectiveStatus
                        )}`}
                      >
                        {displayStatus}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                    <div className="flex items-center gap-1 sm:gap-2 text-gray-700">
                      <FaGamepad className="text-[#0071bc] text-xs sm:text-sm flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 block">
                          Sport
                        </span>
                        <p className="font-semibold text-xs sm:text-sm md:text-base truncate">{request.sport}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 text-gray-700">
                      <FaCalendarAlt className="text-green-500 text-xs sm:text-sm flex-shrink-0" />
                      <div>
                        <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 block">
                          Date
                        </span>
                        <p className="font-semibold text-xs sm:text-sm md:text-base">{request.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 text-gray-700">
                      <FaClock className="text-purple-500 text-xs sm:text-sm flex-shrink-0" />
                      <div>
                        <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 block">
                          Time
                        </span>
                        <p className="font-semibold text-xs sm:text-sm md:text-base">{request.time}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {effectiveStatus === "pending" && (
                  <div className="flex gap-2 sm:gap-3 justify-end">
                    <button
                      onClick={() => onApproveClick(request)}
                      className="bg-green-600 text-white px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium hover:bg-green-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
                    >
                      <FaCheck className="text-xs sm:text-sm" /> <span className="hidden xs:inline">Approve</span>
                    </button>
                    <button
                      onClick={() => onRejectClick(request)}
                      className="bg-red-600 text-white px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium hover:bg-red-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
                    >
                      <FaTimes className="text-xs sm:text-sm" /> <span className="hidden xs:inline">Reject</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredRequests.length === 0 && (
        <div className="text-center py-8 sm:py-12 md:py-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl sm:rounded-2xl border-2 border-dashed border-gray-300">
          <FaExclamationTriangle className="text-4xl sm:text-5xl md:text-6xl text-gray-400 mx-auto mb-2 sm:mb-4" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-1 sm:mb-2">
            No Booking Requests Found
          </h3>
          <p className="text-gray-600 text-xs sm:text-sm md:text-base px-4">
            There are no booking requests matching your current filter.
          </p>
        </div>
      )}

      {/* Confirmation Modal for Approve/Reject Actions */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        title={`${
          actionType === "approve" ? "Approve" : "Reject"
        } Booking Request`}
        message={`Are you sure you want to ${actionType} the booking request from ${selectedRequest?.user} for ${selectedRequest?.sport} on ${selectedRequest?.date} at ${selectedRequest?.time}?`}
        onConfirm={handleConfirmAction}
        onCancel={handleCloseModal}
        confirmText={actionType === "approve" ? "Approve" : "Reject"}
        cancelText="Cancel"
        confirmButtonClass={
          actionType === "approve"
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-red-600 text-white hover:bg-red-700"
        }
      />
    </div>
  );
};

export default BookingRequests;
